/**
 * 회사 상세 대시보드 모달
 * 기업 분석 및 히트맵을 표시하는 모달 컴포넌트
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  Avatar,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Card,
  TextField,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Close,
  TrendingUp,
  LocationOn,
  Engineering,
  Apartment,
  Verified,
  Person,
  Event,
  Search,
  ExpandMore,
  Warning,
  PriorityHigh,
  TrendingUp as TrendingUpIcon,
  HealthAndSafety,
  FactCheck,
  School,
  Construction,
  MedicalServices,
  ReportProblem,
  Settings,
  Home,
  Campaign,
  Lightbulb,
  InfoOutlined,
  Groups,
  HowToReg,
  PersonOff,
  Login,
} from '@mui/icons-material';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { getActivityLogColumnDefs } from './ActivityLogColumnDefs';
import ButtonBasic from '../../../../Components/MUIs/ButtonBasic';
import { Edit } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import {
  ComplianceScoreTooltipContent,
  getContractStatusChipSx,
} from './CUL_BASIC_StatusLegend';

/** 계약 종료일 표시용 (한글·ISO·MM/DD/YYYY 등) */
const formatContractEndDateLabel = (dateStr) => {
  if (!dateStr) return '';
  const s = String(dateStr).trim();
  if (/년/.test(s)) return s.replace(/\s+/g, ' ').trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}년 ${iso[2]}월 ${iso[3]}일`;
  // API: MM/DD/YYYY (CUL_BASICF formatDateForInput과 동일)
  const us = s.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (us) return `${us[3]}년 ${us[1]}월 ${us[2]}일`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}년 ${m}월 ${day}일`;
  }
  return s;
};

const getContractRemainingDays = (contractEndDate, remainingDays) => {
  if (remainingDays !== undefined && remainingDays !== null) {
    const n = Number(remainingDays);
    if (Number.isFinite(n)) return n;
  }
  if (!contractEndDate) return null;
  const dateStr = String(contractEndDate)
    .replace(/년/g, '-')
    .replace(/월/g, '-')
    .replace(/일/g, '')
    .replace(/\s+/g, '')
    .split('-')
    .map((part, idx) => (idx === 0 ? part : part.padStart(2, '0')))
    .join('-');
  const endDate = new Date(dateStr);
  if (Number.isNaN(endDate.getTime())) return null;
  return Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24));
};

const getContractRemainingLabel = (daysRemaining) => {
  if (daysRemaining === null || daysRemaining === undefined) return '';
  if (daysRemaining < 0) return '만료됨';
  if (daysRemaining === 0) return '오늘 만료';
  if (daysRemaining <= 30) return `${daysRemaining}일 후 만료`;
  if (daysRemaining <= 365) {
    return `${Math.floor(daysRemaining / 30)}개월 남음`;
  }
  return `${(daysRemaining / 365).toFixed(1)}년 남음`;
};

const getCompanyInitials = (name) => {
  if (!name) return '?';
  const words = name.split(' ');
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const formatNumber = (num) => Number(num || 0).toLocaleString('ko-KR');

const EMPTY_ACCOUNT_STATS = {
  registeredMemberCount: 0,
  activeAccountCount: 0,
  inactiveAccountCount: 0,
  recentLoginUserCount: 0,
};

const parseAccountStats = (stats) => {
  if (!stats || typeof stats !== 'object') return { ...EMPTY_ACCOUNT_STATS };
  return {
    registeredMemberCount: Number(
      stats.registeredMemberCount ?? stats.RegisteredMemberCount ?? 0
    ),
    activeAccountCount: Number(
      stats.activeAccountCount ?? stats.ActiveAccountCount ?? 0
    ),
    inactiveAccountCount: Number(
      stats.inactiveAccountCount ?? stats.InactiveAccountCount ?? 0
    ),
    recentLoginUserCount: Number(
      stats.recentLoginUserCount ?? stats.RecentLoginUserCount ?? 0
    ),
  };
};

/** 최근 12개월 월별 사용 추이 (빈 달은 0으로 채움) */
const buildMonthlyUsageChartData = (trends) => {
  const map = new Map();
  (Array.isArray(trends) ? trends : []).forEach((row) => {
    const yearMonth = row.yearMonth ?? row.YearMonth;
    if (!yearMonth) return;
    const webUsageCount = Number(
      row.webUsageCount ?? row.WebUsageCount ?? 0
    );
    const mobileLoginCount = Number(
      row.mobileLoginCount ?? row.MobileLoginCount ?? 0
    );
    map.set(yearMonth, {
      yearMonth,
      monthLabel:
        row.monthLabel ??
        row.MonthLabel ??
        String(yearMonth).replace(/^\d{4}-/, '').replace('-', '.'),
      webUsageCount,
      mobileLoginCount,
      usageCount: Number(
        row.usageCount ??
          row.UsageCount ??
          webUsageCount + mobileLoginCount
      ),
      activeUserCount: Number(
        row.activeUserCount ?? row.ActiveUserCount ?? 0
      ),
    });
  });

  const result = [];
  const now = new Date();
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const yearMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = `${String(d.getFullYear()).slice(-2)}.${String(d.getMonth() + 1).padStart(2, '0')}`;
    result.push(
      map.get(yearMonth) ?? {
        yearMonth,
        monthLabel,
        webUsageCount: 0,
        mobileLoginCount: 0,
        usageCount: 0,
        activeUserCount: 0,
      }
    );
  }
  return result;
};

// dailyUsageData 파싱 함수
const parseDailyUsageData = (dailyUsageData) => {
  if (!dailyUsageData || typeof dailyUsageData !== 'string') return [];
  return dailyUsageData.split('|').map((item) => {
    const [date, count] = item.split(':');
    return { date, count: Number(count || 0) };
  });
};

// 스파크라인 바 높이 계산 함수
const getSparklineHeights = (usageData, maxValue) => {
  if (!usageData || usageData.length === 0) return [];
  if (maxValue === 0) return usageData.map(() => 0);

  // 최대 5개 데이터 포인트로 제한
  const dataPoints = usageData.slice(-5);
  return dataPoints.map((item) => {
    const percentage = (item.count / maxValue) * 100;
    return Math.max(20, Math.min(100, percentage));
  });
};

// 카테고리별 아이콘 및 색상 매핑
const getCategoryConfig = (categoryName) => {
  const configs = {
    '안전 관리': { icon: HealthAndSafety, color: 'blue' },
    '점검 및 보고': { icon: FactCheck, color: 'emerald' },
    '교육/훈련': { icon: School, color: 'purple' },
    안전보건관리체계: { icon: HealthAndSafety, color: 'blue' },
    위험성평가: { icon: Warning, color: 'orange' },
    '안전작업 관리': { icon: Construction, color: 'amber' },
    '교육 및 훈련': { icon: School, color: 'purple' },
    보건관리: { icon: MedicalServices, color: 'teal' },
    산업재해: { icon: ReportProblem, color: 'red' },
    기준정보관리: { icon: Settings, color: 'slate' },
    홈: { icon: Home, color: 'blue' },
    '공지/게시': { icon: Campaign, color: 'indigo' },
    '공지 사항': { icon: Campaign, color: 'indigo' },
    안전보건제안: { icon: Lightbulb, color: 'yellow' },
  };

  return configs[categoryName] || { icon: Settings, color: 'slate' };
};

function CompanyDetailModal({
  open,
  onClose,
  companyData,
  detailData,
  onEditContract,
}) {
  const { menuInfo } = useSelector((state) => state.BaseInfo);
  const [timeframe, setTimeframe] = useState('30days');
  const [userSearchText, setUserSearchText] = useState('');
  const [allUsersModalOpen, setAllUsersModalOpen] = useState(false);
  const activityLogGridRef = useRef(null);
  const allUsersGridRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line no-console -- 디버깅용
    console.log('[CompanyDetailModal] companyData', companyData);
  }, [open, companyData]);

  // menuId를 메뉴명으로 변환하는 함수
  const getMenuName = (menuId) => {
    if (!menuId || !menuInfo || !Array.isArray(menuInfo)) return menuId;
    const menu = menuInfo.find(
      (m) => m.menuId === menuId || m.MENU_ID === menuId
    );
    return menu ? menu.menuNm || menu.MENU_NM || menuId : menuId;
  };

  // 기본 데이터
  const defaultData = {
    companyName: companyData?.companyName || 'Acme 건설',
    companyId: companyData?.companyId || '-',
    representativeName: companyData?.representativeName || '',
    contractEndDate: companyData?.contractEndDate || '',
    remainingDays:
      companyData?.remainingDays !== undefined &&
      companyData?.remainingDays !== null
        ? Number(companyData.remainingDays)
        : null,
    address1: companyData?.address1 || '-',
    address2: companyData?.address2 || '-',
    status: '활성 기업',
    location: `${companyData?.address1} ${companyData?.address2}`,
    sinceDate: '',
    totalWorkerCount: Number(
      companyData?.totalWorkerCount ?? companyData?.activeUsers ?? 0
    ),
    custNm: companyData?.custNm || companyData?.CustNm || '',
    complianceScore: 98,
    accountStats: { ...EMPTY_ACCOUNT_STATS },
    monthlyUsageTrends: [],
    totalInteractions: 14205,
    adoptionRate: 85,
    adoptionRateChange: 12,
    activeUsers: [
      {
        name: 'Sarah Jenkins',
        role: '안전 관리자',
        activities: 240,
        avatar: '',
      },
      { name: 'Michael Chen', role: '현장 소장', activities: 189, avatar: '' },
      { name: 'Priya Patel', role: '작업 반장', activities: 156, avatar: '' },
    ],
    activityLogs: [
      {
        id: 1,
        userName: 'Sarah Jenkins',
        activity: '작업 허가서 #PTW-4092 승인',
        module: '작업 허가서',
        time: '2분 전',
        status: '성공',
        userAvatar: '',
      },
      {
        id: 2,
        userName: 'David Kim',
        activity: '위험성 평가 RA-12 제출',
        module: '위험성 평가',
        time: '15분 전',
        status: '대기 중',
        userAvatar: '',
      },
      {
        id: 3,
        userName: 'Michael Chen',
        activity: '일일 TBM 체크리스트 완료',
        module: 'TBM (작업 전 회의)',
        time: '1시간 전',
        status: '성공',
        userAvatar: '',
      },
      {
        id: 4,
        userName: 'Priya Patel',
        activity: '로그인 실패 (3회)',
        module: '인증',
        time: '2시간 전',
        status: '실패',
        userAvatar: '',
      },
    ],
    heatmapData: {
      features: [
        'TBM (작업 전 회의)',
        '위험성 평가',
        '작업 허가서',
        '사고 보고서',
        '안전 점검',
      ],
      dates: ['10월 1일', '10월 8일', '10월 15일', '10월 22일', '10월 29일'],
      data: [
        [2, 1, 4, 1, 2, 2, 0, 4, 2, 1, 2, 4], // TBM
        [4, 4, 2, 2, 1, 0, 2, 2, 4, 4, 2, 2], // 위험성 평가
        [1, 0, 1, 2, 2, 2, 0, 1, 1, 2, 1, 0], // 작업 허가서
        [0, 0, 1, 0, 0, 1, 0, 0, 2, 0, 0, 0], // 사고 보고서
        [2, 2, 4, 4, 2, 2, 4, 4, 2, 2, 4, 2], // 안전 점검
      ],
    },
  };

  // dailyMenuUsages를 히트맵 형식으로 변환
  const convertToHeatmapData = (dailyMenuUsages) => {
    if (!Array.isArray(dailyMenuUsages) || dailyMenuUsages.length === 0) {
      return defaultData.heatmapData;
    }

    // 메뉴별로 그룹화
    const menuMap = new Map();
    const dateSet = new Set();

    dailyMenuUsages.forEach((item) => {
      // menuId를 메뉴명으로 변환
      const menuId =
        item.menuId || item.menuName || item.module || item.feature || '';
      const menuName = getMenuName(menuId);
      const date = item.date || item.actionDate || item.actionDt || '';
      const count = Number(item.count || item.usageCount || item.usage || 0);

      if (!menuName || !date) return; // 필수 필드가 없으면 스킵

      if (!menuMap.has(menuName)) {
        menuMap.set(menuName, new Map());
      }
      menuMap.get(menuName).set(date, count);
      dateSet.add(date);
    });

    // 날짜 정렬
    const sortedDates = Array.from(dateSet).sort();

    // 히트맵 데이터 생성
    const features = Array.from(menuMap.keys());

    // 최대값을 찾아서 정규화 기준 설정
    let maxCount = 0;
    menuMap.forEach((dateMap) => {
      dateMap.forEach((count) => {
        maxCount = Math.max(maxCount, count);
      });
    });

    const data = features.map((menuName) => {
      const dateMap = menuMap.get(menuName);
      return sortedDates.map((date) => {
        const count = dateMap.get(date) || 0;
        // 0-4 범위로 정규화 (히트맵 색상에 맞춤)
        if (maxCount === 0) return 0;
        return Math.min(4, Math.max(0, Math.floor((count / maxCount) * 4)));
      });
    });

    return {
      features,
      dates: sortedDates,
      data,
    };
  };

  // 기능별 스마트 요약 데이터 처리
  const functionalSmartSummary = useMemo(() => {
    // detailData에서 여러 가능한 필드명으로 데이터 찾기
    let rawData = null;

    // 1. 직접 배열로 전달된 경우
    if (Array.isArray(detailData)) {
      rawData = detailData;
    }
    // 2. 객체 내부의 다양한 필드명 확인
    else if (detailData && typeof detailData === 'object') {
      // 가능한 모든 필드명 확인
      rawData =
        detailData.functionalSmartSummaryList ||
        detailData.smartSummaryList ||
        detailData.smartSummary ||
        detailData.functionalSummary ||
        detailData.categoryUsageList ||
        detailData.usageList ||
        detailData.categoryList ||
        detailData.menuUsageList ||
        detailData.data ||
        detailData.list ||
        [];

      // 만약 여전히 찾지 못했다면, detailData의 모든 키를 확인
      if (!rawData || (Array.isArray(rawData) && rawData.length === 0)) {
        // detailData의 모든 값 중 배열인 것을 찾기
        for (const key in detailData) {
          if (Array.isArray(detailData[key]) && detailData[key].length > 0) {
            // 배열의 첫 번째 항목이 categoryName을 가지고 있는지 확인
            const firstItem = detailData[key][0];
            if (
              firstItem &&
              (firstItem.categoryName || firstItem.subCategoryName)
            ) {
              rawData = detailData[key];
              break;
            }
          }
        }
      }
    }

    // 디버깅: 실제 전달된 데이터 확인

    if (!Array.isArray(rawData) || rawData.length === 0) {
      return [];
    }

    // 카테고리별로 그룹화
    const categoryMap = new Map();

    rawData.forEach((item) => {
      const categoryName = item.categoryName || '기타';
      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, {
          categoryName,
          categoryId: item.categoryId || '',
          categorySeq: item.categorySeq || 0,
          topActive: [],
          bottomInactive: [],
          others: [],
          totalUsage: 0,
        });
      }

      const category = categoryMap.get(categoryName);
      const parsedData = {
        ...item,
        dailyUsage: parseDailyUsageData(item.dailyUsageData || ''),
      };

      if (item.sectionType === 'TOP_ACTIVE') {
        category.topActive.push(parsedData);
      } else if (item.sectionType === 'BOTTOM_INACTIVE') {
        category.bottomInactive.push(parsedData);
      } else {
        category.others.push(parsedData);
      }

      category.totalUsage += Number(item.totalUsage || 0);
    });

    // 각 카테고리 내에서 정렬
    categoryMap.forEach((category) => {
      category.topActive.sort(
        (a, b) => Number(b.totalUsage || 0) - Number(a.totalUsage || 0)
      );
      category.bottomInactive.sort(
        (a, b) => Number(a.totalUsage || 0) - Number(b.totalUsage || 0)
      );
    });

    // 카테고리별로 정렬 (categorySeq 기준)
    return Array.from(categoryMap.values()).sort(
      (a, b) => (a.categorySeq || 0) - (b.categorySeq || 0)
    );
  }, [detailData]);

  // 데이터 병합 및 안전성 검사
  const data = useMemo(() => {
    const merged = { ...defaultData, ...companyData };

    // Search2 데이터 적용
    if (detailData) {
      // 회사 정보
      if (detailData.companyInfo) {
        const ci = detailData.companyInfo;
        merged.companyName =
          ci.companyName || ci.CompanyName || merged.companyName;
        merged.address1 = ci.address1;
        merged.address2 = ci.address2;
        merged.location =
          [merged.address1, merged.address2].filter(Boolean).join(' ') ||
          merged.location;
        merged.sinceDate =
          detailData.companyInfo.usageStartDate || merged.sinceDate;
        merged.totalWorkerCount = Number(
          ci.totalWorkerCount ??
            ci.TotalWorkerCount ??
            merged.totalWorkerCount ??
            0
        );
        merged.custNm = String(
          ci.custNm ?? ci.CustNm ?? merged.custNm ?? ''
        ).trim();
        merged.complianceScore = Number(
          detailData.companyInfo.complianceScore || 0
        );
        merged.representativeName = String(
          ci.name ?? ci.Name ?? merged.representativeName ?? ''
        ).trim();
        if (ci.id || ci.Id) {
          merged.companyId = ci.id || ci.Id;
        }
        const rawContractEnd =
          ci.contractEndDate ?? ci.ContractEndDate ?? merged.contractEndDate;
        if (rawContractEnd) {
          merged.contractEndDate =
            formatContractEndDateLabel(rawContractEnd) || merged.contractEndDate;
        }

        // statusBadge를 한글로 변환
        const statusBadge = detailData.companyInfo.statusBadge || '';
        if (statusBadge === 'Active') {
          merged.status = '활성 기업';
        } else if (statusBadge === 'Inactive') {
          merged.status = '비활성 기업';
        } else if (statusBadge === 'Suspended') {
          merged.status = '중지된 기업';
        } else {
          merged.status = statusBadge || merged.status;
        }
      }

      // 총 인터랙션
      if (detailData.totalInteraction !== undefined) {
        merged.totalInteractions = Number(detailData.totalInteraction || 0);
      }

      // 히트맵 데이터 (기존 호환성 유지)
      if (detailData.dailyMenuUsages) {
        merged.heatmapData = convertToHeatmapData(detailData.dailyMenuUsages);
      }

      // 기능별 스마트 요약 데이터가 detailData에 직접 포함된 경우
      // (이미 functionalSmartSummary useMemo에서 처리하지만, 여기서도 확인)
      if (
        detailData.functionalSmartSummaryList ||
        detailData.smartSummaryList
      ) {
        merged.functionalSmartSummaryList =
          detailData.functionalSmartSummaryList || detailData.smartSummaryList;
      }

      if (detailData.accountStats) {
        merged.accountStats = parseAccountStats(detailData.accountStats);
      }

      if (Array.isArray(detailData.monthlyUsageTrends)) {
        merged.monthlyUsageTrends = detailData.monthlyUsageTrends;
      } else if (Array.isArray(detailData.MonthlyUsageTrends)) {
        merged.monthlyUsageTrends = detailData.MonthlyUsageTrends;
      }

      // 도입률
      if (detailData.adoptionRate) {
        merged.adoptionRate = Number(detailData.adoptionRate.currentRate || 0);
        merged.adoptionRateChange = Number(
          detailData.adoptionRate.changeRate || 0
        );
      }

      // 주요 활성 사용자 (최대 3명)
      if (Array.isArray(detailData.topActiveUsers)) {
        merged.activeUsers = detailData.topActiveUsers
          .slice(0, 3)
          .map((user, idx) => ({
            name: user.userName || user.name || '',
            role: user.role || user.position || '',
            activities: Number(user.activities || user.activityCount || 0),
            avatar: user.photoUrl || user.avatar || '',
          }));
        // 전체 사용자 목록도 저장 (모달에서 사용)
        merged.allActiveUsers = detailData.topActiveUsers.map((user, idx) => ({
          name: user.userName || user.name || '',
          role: user.role || user.position || '',
          activities: Number(user.activities || user.activityCount || 0),
          avatar: user.photoUrl || user.avatar || '',
        }));
      }

      // 최근 활동 로그
      if (Array.isArray(detailData.recentActivityLogs)) {
        merged.activityLogs = detailData.recentActivityLogs.map((log, idx) => ({
          id: log.id || idx + 1,
          userName: log.userName || '',
          activity: log.activityContent || log.activity || '',
          activityContent: log.activityContent || log.activity || '', // ActivityLogColumnDefs에서 사용
          module: log.module || '',
          time: log.actionDt || log.time || '',
          actionDt: log.actionDt || log.time || '', // ActivityLogColumnDefs에서 사용
          status: log.status || 'SUCCESS',
          userAvatar: log.photoUrl || log.userAvatar || '',
          photoUrl: log.photoUrl || log.userAvatar || '', // ActivityLogColumnDefs에서 사용
        }));
      }
    }

    // activeUsers가 배열인지 확인
    if (!Array.isArray(merged.activeUsers)) {
      merged.activeUsers = defaultData.activeUsers;
    }

    // allActiveUsers가 없으면 activeUsers를 사용
    if (!Array.isArray(merged.allActiveUsers)) {
      merged.allActiveUsers = merged.activeUsers || [];
    }

    // activityLogs가 배열인지 확인
    if (!Array.isArray(merged.activityLogs)) {
      merged.activityLogs = defaultData.activityLogs;
    }

    // heatmapData가 올바른 구조인지 확인
    if (!merged.heatmapData || !Array.isArray(merged.heatmapData.features)) {
      merged.heatmapData = defaultData.heatmapData;
    }

    return merged;
  }, [companyData, detailData]);

  const contractEndRemainingDays = useMemo(
    () =>
      getContractRemainingDays(data.contractEndDate, data.remainingDays),
    [data.contractEndDate, data.remainingDays]
  );

  const contractEndRemainingLabel = useMemo(
    () => getContractRemainingLabel(contractEndRemainingDays),
    [contractEndRemainingDays]
  );

  const isContractExpiringSoon =
    contractEndRemainingDays !== null && contractEndRemainingDays <= 30;

  const accountStats = data.accountStats ?? EMPTY_ACCOUNT_STATS;

  const monthlyUsageChartData = useMemo(
    () => buildMonthlyUsageChartData(data.monthlyUsageTrends),
    [data.monthlyUsageTrends]
  );

  const accountKpiItems = useMemo(
    () => [
      {
        key: 'registered',
        label: '등록 인원수',
        value: accountStats.registeredMemberCount,
        icon: Groups,
        color: '#137fec',
        hint: '승인(USE_YN=Y) · 삭제 제외 회원',
      },
      {
        key: 'active',
        label: '활성 계정 수',
        value: accountStats.activeAccountCount,
        icon: HowToReg,
        color: '#10b981',
        hint: '최근 30일 웹(저장 로그) 또는 모바일(FCM 로그인) 접속 회원',
      },
      {
        key: 'inactive',
        label: '미사용 계정 수',
        value: accountStats.inactiveAccountCount,
        icon: PersonOff,
        color: '#f59e0b',
        hint: '등록 인원 중 30일간 웹·모바일 접속 이력 없음',
      },
      {
        key: 'recentLogin',
        label: '최근 접속자 수',
        value: accountStats.recentLoginUserCount,
        icon: Login,
        color: '#8b5cf6',
        hint: '최근 7일 웹(저장 로그) 또는 모바일(FCM 로그인) 접속 회원',
      },
    ],
    [accountStats]
  );

  // 활동 로그 컬럼 정의
  const activityLogColumnDefs = useMemo(() => getActivityLogColumnDefs(), []);

  // 활동 로그 rowData (검색 필터 적용)
  const activityLogRowData = useMemo(() => {
    if (!Array.isArray(data.activityLogs)) {
      return [];
    }

    let filteredLogs = data.activityLogs;

    // 사용자 검색 필터 적용
    if (userSearchText) {
      const searchLower = userSearchText.toLowerCase();
      filteredLogs = filteredLogs.filter((log) => {
        const userName = (log.userName || '').toLowerCase();
        return userName.includes(searchLower);
      });
    }

    return filteredLogs.map((log, idx) => ({
      ...log,
      id: log.id || idx,
    }));
  }, [data.activityLogs, userSearchText]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '95vh',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
            {data.companyName || companyData?.companyName || '회사'} 상세 분석
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: 13,
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            기능별 이용 요약 · 도입률 · 활동 로그 · 주요 사용자
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="닫기"
          sx={{
            color: 'text.secondary',
            flexShrink: 0,
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(95vh - 80px)',
        }}
      >
        <Box
          sx={{
            p: 4,
            overflowY: 'auto',
            overflowX: 'hidden',
            flex: 1,
          }}
        >
          {/* 회사 정보 헤더 */}
          <Card
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                gap: 3,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2.5 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    bgcolor: 'rgba(19, 127, 236, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 32,
                      fontWeight: 700,
                      color: 'primary.main',
                    }}
                  >
                    {getCompanyInitials(data.companyName)}
                  </Typography>
                </Box>
                <Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 0.5,
                    }}
                  >
                    <Typography sx={{ fontSize: 24, fontWeight: 700 }}>
                      {data.companyName}
                    </Typography>
                    {(companyData?.status || data.status) && (
                      <Chip
                        label={companyData?.status || data.status}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          ...getContractStatusChipSx(
                            companyData?.status || data.status
                          ),
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 0.5,
                      mb: 0.5,
                    }}
                  >
                    <LocationOn sx={{ fontSize: 18, mt: 0.15 }} />
                    주소 :{' '}
                    <Box
                      component="span"
                      sx={{ color: 'text.primary', fontWeight: 600 }}
                    >
                      {[data.address1, data.address2].filter(Boolean).join(' ') ||
                        '-'}
                    </Box>
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 14,
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 1,
                    }}
                  >
                    <Person sx={{ fontSize: 18 }} />
                    대표자:{' '}
                    <Box
                      component="span"
                      sx={{ color: 'text.primary', fontWeight: 700 }}
                    >
                      {data.representativeName ||
                        companyData?.representativeName ||
                        '-'}
                    </Box>
                    {(data.companyId || companyData?.companyId) &&
                    (data.companyId || companyData?.companyId) !== '-' ? (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'text.secondary',
                        }}
                      >
                        (ID: {data.companyId || companyData?.companyId})
                      </Box>
                    ) : null}
                  </Typography>
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: 'text.secondary',
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 0.5,
                        mb: data.sinceDate ? 0.35 : 0,
                      }}
                    >
                      <Event sx={{ fontSize: 18 }} />
                      계약 종료:{' '}
                      <Box
                        component="span"
                        sx={{
                          color: isContractExpiringSoon
                            ? 'error.main'
                            : 'text.primary',
                          fontWeight: 700,
                        }}
                      >
                        {formatContractEndDateLabel(
                          data.contractEndDate ||
                            companyData?.contractEndDate ||
                            ''
                        ) || '-'}
                      </Box>
                      {contractEndRemainingLabel ? (
                        <Box
                          component="span"
                          sx={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: isContractExpiringSoon
                              ? 'error.main'
                              : 'text.secondary',
                          }}
                        >
                          · {contractEndRemainingLabel}
                        </Box>
                      ) : null}
                    </Typography>
                    {data.sinceDate ? (
                      <Typography
                        sx={{
                          fontSize: 13,
                          color: 'text.secondary',
                          pl: 3.25,
                          lineHeight: 1.5,
                        }}
                      >
                        {data.sinceDate}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'text.primary',
                      }}
                    >
                      <Apartment sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        사업장 :{' '}
                        <strong>
                          {data.custNm || companyData?.custNm || '-'}
                        </strong>
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'text.primary',
                      }}
                    >
                      <Engineering
                        sx={{ fontSize: 20, color: 'primary.main' }}
                      />
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        총 계약 인원 수 :{' '}
                        <strong>{formatNumber(data.totalWorkerCount)}</strong>
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'text.primary',
                      }}
                    >
                      <Verified sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        <strong>{data.complianceScore}%</strong> 준수 점수
                      </Typography>
                      <Tooltip
                        title={<ComplianceScoreTooltipContent />}
                        arrow
                        placement="top"
                        enterTouchDelay={0}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: 'rgba(15, 23, 42, 0.96)',
                              maxWidth: 360,
                              p: 1.25,
                            },
                          },
                        }}
                      >
                        <IconButton
                          size="small"
                          aria-label="준수 점수 산정 기준 안내"
                          sx={{
                            p: 0.25,
                            color: 'text.secondary',
                            '&:hover': { color: 'primary.main' },
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <InfoOutlined sx={{ fontSize: 17 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1.5,
                }}
              >
                <ButtonBasic
                  name="계약 정보 수정"
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => {
                    if (onEditContract) {
                      onEditContract(companyData, detailData);
                    }
                  }}
                  sx={{
                    borderColor: 'rgba(148, 163, 184, 0.3)',
                    color: 'text.primary',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'rgba(19,127,236,0.04)',
                    },
                  }}
                />
              </Box>
            </Box>
          </Card>

          <Card
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 0.5 }}>
              계정 · 이용 현황
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: 'text.secondary', mb: 2.5 }}
            >
              등록·활성·미사용·접속은 승인 회원 기준입니다. 웹(관리자)은
              PROC_ACTION_LOG, 모바일(근로자·관리자 등)은 TB_BS_MEMBER_FCM
              로그인 시각을 합산합니다.
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {accountKpiItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Grid item xs={6} md={3} key={item.key}>
                    <Tooltip title={item.hint} arrow placement="top">
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          border: '1px solid rgba(148,163,184,0.25)',
                          bgcolor: 'rgba(15,23,42,0.02)',
                          height: '100%',
                          cursor: 'help',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: `${item.color}18`,
                              color: item.color,
                            }}
                          >
                            <Icon sx={{ fontSize: 20 }} />
                          </Box>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: 'text.secondary',
                            }}
                          >
                            {item.label}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 26,
                            fontWeight: 800,
                            color: 'text.primary',
                            lineHeight: 1.1,
                          }}
                        >
                          {formatNumber(item.value)}
                          <Box
                            component="span"
                            sx={{
                              fontSize: 14,
                              fontWeight: 600,
                              ml: 0.5,
                              color: 'text.secondary',
                            }}
                          >
                            명
                          </Box>
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Grid>
                );
              })}
            </Grid>

            <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>
              월별 사용 추이 ( 최근 12개월 )
            </Typography>
            <Typography
              sx={{ fontSize: 13, color: 'text.secondary', mb: 2 }}
            >
              막대: 웹 액션 수 + 모바일 로그인 건수 /  선: 활성 사용자
              (웹·모바일 합산, 중복 제외)
            </Typography>
            <Box
              sx={{
                height: 300,
                borderRadius: 2,
                border: '1px dashed rgba(148,163,184,0.45)',
                bgcolor: 'rgba(15,23,42,0.02)',
                p: 2,
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={monthlyUsageChartData}
                  margin={{ left: 4, right: 12, top: 8, bottom: 4 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(148,163,184,0.25)"
                  />
                  <XAxis dataKey="monthLabel" tick={{ fontSize: 11 }} />
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <ReTooltip
                    formatter={(value, name) => {
                      const unit = name === '활성 사용자' ? '명' : '건';
                      return [`${formatNumber(value)}${unit}`, name];
                    }}
                    labelFormatter={(label) => `월: ${label}`}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 12 }}
                    formatter={(value) => value}
                  />
                  <Bar
                    yAxisId="left"
                    stackId="usage"
                    name="웹(저장)"
                    dataKey="webUsageCount"
                    fill="#137fec"
                    maxBarSize={40}
                  />
                  <Bar
                    yAxisId="left"
                    stackId="usage"
                    name="모바일(로그인)"
                    dataKey="mobileLoginCount"
                    fill="#8b5cf6"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={40}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    name="활성 사용자"
                    dataKey="activeUserCount"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Card>

          <Grid container spacing={3}>
            {/* 왼쪽: 기능별 스마트 요약 리스트 */}
            <Grid item xs={12} xl={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                    기능별 스마트 요약 리스트
                  </Typography>
                </Box>
                <Card
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                    overflow: 'hidden',
                  }}
                >
                  {functionalSmartSummary.length > 0 ? (
                    functionalSmartSummary.map((category, categoryIdx) => {
                      const config = getCategoryConfig(category.categoryName);
                      const colorMap = {
                        blue: {
                          bg: 'rgba(59, 130, 246, 0.1)',
                          text: '#3b82f6',
                          darkBg: 'rgba(59, 130, 246, 0.3)',
                        },
                        emerald: {
                          bg: 'rgba(16, 185, 129, 0.1)',
                          text: '#10b981',
                          darkBg: 'rgba(16, 185, 129, 0.3)',
                        },
                        purple: {
                          bg: 'rgba(168, 85, 247, 0.1)',
                          text: '#a855f7',
                          darkBg: 'rgba(168, 85, 247, 0.3)',
                        },
                        orange: {
                          bg: 'rgba(249, 115, 22, 0.1)',
                          text: '#f97316',
                          darkBg: 'rgba(249, 115, 22, 0.3)',
                        },
                        amber: {
                          bg: 'rgba(245, 158, 11, 0.1)',
                          text: '#f59e0b',
                          darkBg: 'rgba(245, 158, 11, 0.3)',
                        },
                        teal: {
                          bg: 'rgba(20, 184, 166, 0.1)',
                          text: '#14b8a6',
                          darkBg: 'rgba(20, 184, 166, 0.3)',
                        },
                        red: {
                          bg: 'rgba(239, 68, 68, 0.1)',
                          text: '#ef4444',
                          darkBg: 'rgba(239, 68, 68, 0.3)',
                        },
                        slate: {
                          bg: 'rgba(100, 116, 139, 0.1)',
                          text: '#64748b',
                          darkBg: 'rgba(100, 116, 139, 0.3)',
                        },
                        indigo: {
                          bg: 'rgba(99, 102, 241, 0.1)',
                          text: '#6366f1',
                          darkBg: 'rgba(99, 102, 241, 0.3)',
                        },
                        yellow: {
                          bg: 'rgba(234, 179, 8, 0.1)',
                          text: '#eab308',
                          darkBg: 'rgba(234, 179, 8, 0.3)',
                        },
                      };
                      const colors = colorMap[config.color] || colorMap.slate;
                      const topActive = category.topActive.slice(0, 3);
                      const bottomInactive = category.bottomInactive.slice(
                        0,
                        3
                      );
                      const maxUsage = Math.max(
                        ...topActive.map((item) =>
                          Number(item.totalUsage || 0)
                        ),
                        ...bottomInactive.map((item) =>
                          Number(item.totalUsage || 0)
                        ),
                        1
                      );

                      return (
                        <Accordion
                          key={categoryIdx}
                          defaultExpanded={categoryIdx === 0}
                          sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' },
                            '&:before': { display: 'none' },
                            boxShadow: 'none',
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                              px: 2,
                              py: 1.5,
                              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                              '& .MuiAccordionSummary-content': {
                                alignItems: 'center',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                flex: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  bgcolor: colors.bg,
                                  color: colors.text,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {React.createElement(config.icon, {
                                  sx: { fontSize: 20 },
                                })}
                              </Box>
                              <Box>
                                <Typography
                                  sx={{ fontSize: 14, fontWeight: 700 }}
                                >
                                  {category.categoryName}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: 11, color: 'text.secondary' }}
                                >
                                  {category.categoryName}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                mr: 2,
                              }}
                            >
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography
                                  sx={{ fontSize: 14, fontWeight: 700 }}
                                >
                                  {formatNumber(category.totalUsage)}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: 11, color: 'text.secondary' }}
                                >
                                  총 사용량
                                </Typography>
                              </Box>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails
                            sx={{
                              px: 2,
                              pb: 2,
                              pt: 0.5,
                              bgcolor: 'rgba(0, 0, 0, 0.01)',
                            }}
                          >
                            <Grid container spacing={3} sx={{ mt: 0.5 }}>
                              {/* 가장 활발한 메뉴 Top 3 */}
                              {topActive.length > 0 && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography
                                      sx={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: '#10b981',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        mb: 1,
                                      }}
                                    >
                                      <TrendingUpIcon sx={{ fontSize: 14 }} />
                                      가장 활발한 메뉴 Top 3
                                    </Typography>
                                  </Box>
                                  {topActive.map((item, idx) => {
                                    const heights = getSparklineHeights(
                                      item.dailyUsage,
                                      maxUsage
                                    );
                                    return (
                                      <Card
                                        key={idx}
                                        sx={{
                                          p: 1.5,
                                          mb: 1.5,
                                          border: '1px solid',
                                          borderColor: 'divider',
                                          bgcolor: 'background.paper',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            mb: 1,
                                          }}
                                        >
                                          <Box>
                                            <Typography
                                              sx={{
                                                fontSize: 13,
                                                fontWeight: 500,
                                              }}
                                            >
                                              {item.subCategoryName ||
                                                item.categoryName}
                                            </Typography>
                                            <Typography
                                              sx={{
                                                fontSize: 11,
                                                color: 'text.secondary',
                                              }}
                                            >
                                              {item.subCategoryName ||
                                                item.categoryName}
                                            </Typography>
                                          </Box>
                                          <Box sx={{ textAlign: 'right' }}>
                                            <Typography
                                              sx={{
                                                fontSize: 13,
                                                fontWeight: 700,
                                              }}
                                            >
                                              {formatNumber(item.totalUsage)}
                                            </Typography>
                                            <Typography
                                              sx={{
                                                fontSize: 11,
                                                color: '#10b981',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'flex-end',
                                                gap: 0.5,
                                              }}
                                            >
                                              <TrendingUpIcon
                                                sx={{ fontSize: 12 }}
                                              />
                                              {Math.floor(Math.random() * 15) +
                                                1}
                                              %
                                            </Typography>
                                          </Box>
                                        </Box>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'flex-end',
                                            gap: 0.5,
                                            height: 32,
                                          }}
                                        >
                                          {heights.map((height, hIdx) => (
                                            <Box
                                              key={hIdx}
                                              sx={{
                                                flex: 1,
                                                height: `${height}%`,
                                                bgcolor: `rgba(16, 185, 129, ${0.2 + hIdx * 0.2})`,
                                                borderRadius: 0.5,
                                                minHeight: 4,
                                              }}
                                            />
                                          ))}
                                        </Box>
                                      </Card>
                                    );
                                  })}
                                </Grid>
                              )}

                              {/* 사용률 저조 메뉴 Top 3 */}
                              {bottomInactive.length > 0 && (
                                <Grid item xs={12} md={6}>
                                  <Box sx={{ mb: 1 }}>
                                    <Typography
                                      sx={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        mb: 1,
                                      }}
                                    >
                                      <Warning
                                        sx={{ fontSize: 14, color: '#f97316' }}
                                      />
                                      사용률 저조 메뉴 Top 3
                                    </Typography>
                                  </Box>
                                  {bottomInactive.map((item, idx) => (
                                    <Card
                                      key={idx}
                                      sx={{
                                        p: 1.5,
                                        mb: 1.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'background.paper',
                                        opacity: 0.9,
                                        '&:hover': { opacity: 1 },
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'flex-start',
                                          gap: 1.5,
                                        }}
                                      >
                                        <PriorityHigh
                                          sx={{
                                            fontSize: 18,
                                            color: '#fb923c',
                                            mt: 0.5,
                                          }}
                                        />
                                        <Box sx={{ flex: 1 }}>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'flex-start',
                                            }}
                                          >
                                            <Typography
                                              sx={{
                                                fontSize: 13,
                                                fontWeight: 500,
                                                color: 'text.secondary',
                                              }}
                                            >
                                              {item.subCategoryName ||
                                                item.categoryName}
                                            </Typography>
                                            <Typography
                                              sx={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                color: 'text.secondary',
                                              }}
                                            >
                                              {formatNumber(item.totalUsage)}회
                                            </Typography>
                                          </Box>
                                          <Typography
                                            sx={{
                                              fontSize: 11,
                                              color: 'text.secondary',
                                              mt: 0.5,
                                            }}
                                          >
                                            {item.totalUsage === 0
                                              ? '이번 달 사용 기록이 없습니다.'
                                              : item.totalUsage < 20
                                                ? '최근 30일간 사용량이 매우 저조합니다.'
                                                : '분기 목표 대비 낮은 수준입니다.'}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Card>
                                  ))}
                                </Grid>
                              )}
                            </Grid>
                            {category.topActive.length > 3 ||
                            category.bottomInactive.length > 3 ||
                            category.others.length > 0 ? (
                              <Button
                                fullWidth
                                sx={{
                                  mt: 2,
                                  py: 1,
                                  fontSize: 11,
                                  fontWeight: 500,
                                  color: 'text.secondary',
                                  border: '1px dashed',
                                  borderColor: 'divider',
                                  '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                                    color: 'primary.main',
                                  },
                                }}
                                endIcon={<ExpandMore />}
                              >
                                더 보기 (+
                                {Math.max(0, category.topActive.length - 3) +
                                  Math.max(
                                    0,
                                    category.bottomInactive.length - 3
                                  ) +
                                  category.others.length}
                                개)
                              </Button>
                            ) : null}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })
                  ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography sx={{ color: 'text.secondary' }}>
                        기능별 사용 데이터가 없습니다.
                      </Typography>
                    </Box>
                  )}
                </Card>
              </Box>
            </Grid>

            {/* 오른쪽: 주요 통계 */}
            <Grid item xs={12} xl={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Card
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: 'rgba(16, 185, 129, 0.1)',
                        color: '#10b981',
                      }}
                    >
                      <TrendingUp />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        도입률
                      </Typography>
                      <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                        {Number(data.adoptionRate) >= 70
                          ? '높음'
                          : Number(data.adoptionRate) >= 30
                            ? '보통'
                            : '낮음'}{' '}
                        ({Number(data.adoptionRate || 0).toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      height: 6,
                      width: '100%',
                      borderRadius: 10,
                      bgcolor: 'rgba(148, 163, 184, 0.2)',
                      overflow: 'hidden',
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${Math.min(100, Math.max(0, Number(data.adoptionRate || 0)))}%`,
                        bgcolor: '#10b981',
                        borderRadius: 10,
                      }}
                    />
                  </Box>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                    {data.adoptionRateChange > 0 ? '+' : ''}
                    {data.adoptionRateChange}% 지난달 대비
                  </Typography>
                </Card>

                <Card
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                    flex: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
                    주요 활성 사용자
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                  >
                    {Array.isArray(data.activeUsers) &&
                    data.activeUsers.length > 0 ? (
                      data.activeUsers.map((user, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: 'primary.main',
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                            src={user.avatar}
                          >
                            {getCompanyInitials(user.name)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {user.name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {user.role}
                            </Typography>
                          </Box>
                          <Chip
                            label={`${user.activities} 활동`}
                            size="small"
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              bgcolor: 'rgba(19, 127, 236, 0.1)',
                              color: 'primary.main',
                              height: 24,
                            }}
                          />
                        </Box>
                      ))
                    ) : (
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: 'text.secondary',
                          textAlign: 'center',
                          py: 2,
                        }}
                      >
                        활성 사용자 데이터가 없습니다.
                      </Typography>
                    )}
                  </Box>
                  <ButtonBasic
                    name="전체 사용자 보기"
                    variant="text"
                    onClick={() => setAllUsersModalOpen(true)}
                    sx={{
                      mt: 2,
                      width: '100%',
                      fontSize: 12,
                      fontWeight: 500,
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'rgba(19, 127, 236, 0.04)',
                      },
                    }}
                  />
                </Card>
              </Box>
            </Grid>
          </Grid>

          {/* 최근 활동 로그 */}
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                gap: 2,
              }}
            >
              <Typography sx={{ fontSize: 18, fontWeight: 700 }}>
                최근 활동 로그
              </Typography>
              <TextField
                placeholder="사용자명으로 검색..."
                value={userSearchText}
                onChange={(e) => {
                  setUserSearchText(e.target.value);
                  // ag-grid quickFilter도 적용
                  if (activityLogGridRef.current?.api) {
                    activityLogGridRef.current.api.setQuickFilter(
                      e.target.value
                    );
                  }
                }}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 250,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(248,250,252,1)',
                    '&:hover': {
                      bgcolor: 'rgba(241,245,249,1)',
                    },
                  },
                }}
              />
            </Box>
            <Card
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
                overflow: 'hidden',
              }}
            >
              <div
                className="ag-theme-alpine"
                style={{
                  height: '400px',
                  width: '100%',
                }}
              >
                <AgGridReact
                  ref={activityLogGridRef}
                  rowData={activityLogRowData}
                  columnDefs={activityLogColumnDefs}
                  getRowId={(params) => params.data.id}
                  defaultColDef={{
                    resizable: true,
                    sortable: true,
                    filter: true,
                  }}
                  pagination={true}
                  paginationPageSize={10}
                  localeText={{
                    page: '페이지',
                    more: '더보기',
                    to: '~',
                    of: '/',
                    next: '다음',
                    last: '마지막',
                    first: '첫',
                    previous: '이전',
                    loadingOoo: '로딩 중...',
                    noRowsToShow: '데이터가 없습니다',
                    pageSizeSelectorLabel: '페이지 크기:',
                  }}
                  animateRows={true}
                  suppressCellFocus={true}
                />
              </div>
            </Card>
          </Box>
        </Box>
      </DialogContent>

      {/* 전체 사용자 보기 모달 */}
      <Dialog
        open={allUsersModalOpen}
        onClose={() => setAllUsersModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: '1px solid rgba(231,237,243,1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(231,237,243,1)',
            px: 3,
            py: 2,
          }}
        >
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700 }}>
            전체 활성 사용자
          </Typography>
          <IconButton
            onClick={() => setAllUsersModalOpen(false)}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <div
            className="ag-theme-alpine"
            style={{
              height: '500px',
              width: '100%',
            }}
          >
            <AgGridReact
              ref={allUsersGridRef}
              rowData={data.allActiveUsers || []}
              columnDefs={[
                {
                  field: 'name',
                  headerName: '사용자명',
                  cellRenderer: (params) => {
                    const user = params.data;
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          py: 1,
                        }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: 'primary.main',
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                          src={user.avatar}
                        >
                          {getCompanyInitials(user.name)}
                        </Avatar>
                        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                          {user.name || '-'}
                        </Typography>
                      </Box>
                    );
                  },
                  flex: 1,
                  minWidth: 200,
                },
                {
                  field: 'role',
                  headerName: '직책',
                  flex: 1,
                  minWidth: 150,
                },
                {
                  field: 'activities',
                  headerName: '활동 수',
                  cellRenderer: (params) => {
                    const activities = Number(params.value || 0);
                    return (
                      <Chip
                        label={`${activities} 활동`}
                        size="small"
                        sx={{
                          fontSize: 11,
                          fontWeight: 700,
                          bgcolor: 'rgba(19, 127, 236, 0.1)',
                          color: 'primary.main',
                          height: 24,
                        }}
                      />
                    );
                  },
                  flex: 1,
                  minWidth: 120,
                },
              ]}
              defaultColDef={{
                resizable: true,
                sortable: true,
                filter: true,
              }}
              pagination={true}
              paginationPageSize={10}
              localeText={{
                page: '페이지',
                more: '더보기',
                to: '~',
                of: '/',
                next: '다음',
                last: '마지막',
                first: '첫',
                previous: '이전',
                loadingOoo: '로딩 중...',
                noRowsToShow: '데이터가 없습니다',
                pageSizeSelectorLabel: '페이지 크기:',
              }}
              animateRows={true}
              suppressCellFocus={true}
            />
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default CompanyDetailModal;
